import { lazy, useCallback, useState, Suspense, type JSX, type ReactNode } from "react";
import { ErrorBoundary } from "../ErrorBoundary";
import { OfflineIndicator } from "../features/accessibility/OfflineIndicator";
import { PlayerScreen } from "../features/player/PlayerScreen";
import { ReaderHeaderSettingsButton } from "../features/reader-style/ReaderHeaderSettingsButton";
import { ReaderNavMenu } from "../features/reader-nav/ReaderNavMenu";

const AccessibilityStatementPage = lazy(() =>
  import("../features/accessibility/AccessibilityStatementPage").then((module) => ({
    default: module.AccessibilityStatementPage,
  })),
);
const ScenarioBuilderApp = lazy(() =>
  import("../scenario-builder/ScenarioBuilderApp").then((module) => ({
    default: module.ScenarioBuilderApp,
  })),
);
const CreatorDashboardApp = lazy(() =>
  import("../creator/CreatorDashboardApp").then((module) => ({
    default: module.CreatorDashboardApp,
  })),
);
const ScenarioSearchPage = lazy(() =>
  import("../features/scenario-search/ScenarioSearchPage").then((module) => ({
    default: module.ScenarioSearchPage,
  })),
);
const HomePage = lazy(() =>
  import("../features/home/HomePage").then((module) => ({
    default: module.HomePage,
  })),
);
const ScenarioLandingPage = lazy(() =>
  import("../features/scenario-landing/ScenarioLandingPage").then((module) => ({
    default: module.ScenarioLandingPage,
  })),
);
const LibraryPage = lazy(() =>
  import("../features/library/LibraryPage").then((module) => ({
    default: module.LibraryPage,
  })),
);
const NotificationsPage = lazy(() =>
  import("../features/notifications/NotificationsPage").then((module) => ({
    default: module.NotificationsPage,
  })),
);

function LazyRoute({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ErrorBoundary>
      <OfflineIndicator />
      <Suspense fallback={null}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export function AccessibilityRoute(): JSX.Element {
  return <LazyRoute><AccessibilityStatementPage /></LazyRoute>;
}

export function ScenarioBuilderRoute({
  universeId,
}: {
  universeId: string | null;
}): JSX.Element {
  return <LazyRoute><ScenarioBuilderApp initialUniverseId={universeId} /></LazyRoute>;
}

export function CreatorRoute(): JSX.Element {
  return <LazyRoute><CreatorDashboardApp /></LazyRoute>;
}

export function SearchRoute(): JSX.Element {
  return <LazyRoute><ScenarioSearchPage /></LazyRoute>;
}

export function HomeRoute(): JSX.Element {
  return <LazyRoute><HomePage /></LazyRoute>;
}

export function ScenarioLandingRoute(): JSX.Element {
  return <LazyRoute><ScenarioLandingPage /></LazyRoute>;
}

export function LibraryRoute(): JSX.Element {
  return <LazyRoute><LibraryPage /></LazyRoute>;
}

export function NotificationsRoute(): JSX.Element {
  return <LazyRoute><NotificationsPage /></LazyRoute>;
}

export function PlayerRoute(): JSX.Element {
  const [playerBackground, setPlayerBackground] = useState<{
    scenarioId: string;
    color: string;
  } | null>(null);
  const handlePlayerBackgroundColorChange = useCallback(
    (scenarioId: string, color: string | null): void => {
      setPlayerBackground((current) => {
        if (color === null) {
          return current?.scenarioId === scenarioId ? null : current;
        }
        return { scenarioId, color };
      });
    },
    [],
  );

  return (
    <ErrorBoundary>
      <OfflineIndicator />
      <main
        className="min-h-screen bg-reader-page text-reader-primary"
        style={{
          ...(playerBackground ? { backgroundColor: playerBackground.color } : {}),
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >
        <div className="mx-auto w-full max-w-[1600px] px-4 py-6 text-base md:px-6 md:py-10 md:text-lg">
          <PlayerScreen onPlayerBackgroundColorChange={handlePlayerBackgroundColorChange} />
        </div>
        <ReaderNavMenu />
        <ReaderHeaderSettingsButton />
      </main>
    </ErrorBoundary>
  );
}
